import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Link as MuiLink, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  ContentCopy as CopyIcon,
  Description as PlanIcon,
  Subject as LogsIcon,
  Terminal as TerminalIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { DeploymentRecord } from '../../types/DeploymentRecord';
import { getColors } from '../theme/tokens';
import { buildDeployCommand, isDestroyOperation } from '../../utils/serviceParser';
import { alpha, getStatusHue, statusHues, tagStyle } from '../theme/statusTokens';
import { duration, easing, fadeSlideUp, flash, reduceMotionSx } from '../theme/motion';
import { useLockFor, usePlanLinkFor, useRowFlash } from '../hooks/useDeploymentData';
import { ColumnKey, ColumnWidths } from '../hooks/useColumnWidths';
import ColumnResizeHandle from './ColumnResizeHandle';
import StatusDot from './StatusDot';
import StatusPill from './StatusPill';
import WatchPill from './WatchPill';
import LockBadge from './LockBadge';
import DeploymentProgress, { hasProgressView } from './DeploymentProgress';
import TypeBadge from './TypeBadge';
import EnvBadge from './EnvBadge';
import DeploymentError from './DeploymentError';
import StartRcaButton from './StartRcaButton';
import { formatTimestamp, timeAgo } from '../lib/format';
import { describeError } from '../lib/errorFormat';
import { isInProgressStatus } from '../lib/grouping';
import { serviceHref } from '../lib/records';

/** Hardcoded defaults — consumers that pass no `widths` render exactly as before. */
export const DEFAULT_COLUMN_WIDTHS: ColumnWidths = {
  service: 260,
  version: 110,
  time: 130,
};

// Responsive `display` rules shared by the row and the header so the header
// cells hide in lockstep with their row counterparts.
const VERSION_DISPLAY = { xs: 'none', sm: 'block' } as const;
const BADGES_DISPLAY = { xs: 'none', md: 'flex' } as const;

export interface DeploymentRowProps {
  record: DeploymentRecord;
  /** Hide the app/service column (e.g. on a service page where it's implied). */
  hideService?: boolean;
  /** Hide the env column (e.g. on an environment page). */
  hideEnv?: boolean;
  /** Per-column pixel widths. Omitted → defaults (current hardcoded values). */
  widths?: Partial<ColumnWidths>;
  /**
   * Show a muted second line with approval info, stage/step, destroy marker,
   * and error details (used by the service deployment history).
   */
  details?: boolean;
  /**
   * Show the deployment pipeline stepper below the row for in-progress / failed
   * records (used by detail views — Overview "Deploying now" and ServicePage).
   */
  progress?: boolean;
  /**
   * Position in its list — drives a small staggered entrance on mount. Capped,
   * so long lists still finish their cascade quickly.
   */
  index?: number;
}

export const servicePath = (record: DeploymentRecord): string | null => {
  if (!record.app) return null;
  const job = record.serviceName || record.job_id;
  return job
    ? serviceHref(record.app, job, record.type)
    : `/services/${encodeURIComponent(record.app)}`;
};

export interface DeploymentRowHeaderProps {
  widths?: Partial<ColumnWidths>;
  onResize: (key: ColumnKey, px: number) => void;
  onReset: (key: ColumnKey) => void;
}

/**
 * Slim label header for the Activity feed with drag handles to resize the
 * fixed-width columns. Mirrors DeploymentRow's layout and breakpoints exactly.
 */
export const DeploymentRowHeader: React.FC<DeploymentRowHeaderProps> = ({
  widths,
  onResize,
  onReset,
}) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const w = { ...DEFAULT_COLUMN_WIDTHS, ...widths };

  const labelSx = {
    color: c.inkMuted,
    fontWeight: 600,
    fontSize: '0.68rem',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  };

  const handle = (key: ColumnKey, side: 'left' | 'right') => (
    <ColumnResizeHandle
      width={w[key]}
      onResize={px => onResize(key, px)}
      onReset={() => onReset(key)}
      side={side}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 0.75,
        borderBottom: `1px solid ${c.hairline}`,
        backgroundColor: c.canvasSoft,
        minWidth: 0,
      }}
    >
      {/* StatusDot slot */}
      <Box sx={{ width: 8, flexShrink: 0 }} />

      <Box
        sx={{
          position: 'relative',
          minWidth: 0,
          width: { xs: 'auto', sm: w.service },
          flexShrink: 0,
        }}
      >
        <Typography sx={labelSx} noWrap>
          Service
        </Typography>
        {handle('service', 'right')}
      </Box>

      <Box sx={{ position: 'relative', width: w.version, flexShrink: 0, display: VERSION_DISPLAY }}>
        <Typography sx={labelSx} noWrap>
          Version
        </Typography>
        {handle('version', 'right')}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ position: 'relative', width: w.time, textAlign: 'right', flexShrink: 0 }}>
        <Typography sx={labelSx} noWrap>
          Time
        </Typography>
        {handle('time', 'left')}
      </Box>
    </Box>
  );
};

/**
 * One deployment event as a quiet hairline-separated row. Used by the
 * Activity feed, service history, and the Overview recent list.
 */
const DeploymentRow: React.FC<DeploymentRowProps> = ({
  record,
  hideService,
  hideEnv,
  widths,
  details,
  progress,
  index = 0,
}) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const flashed = useRowFlash(record.id);
  const lock = useLockFor(record);
  // This record's own s3_plan_link is often empty while a terraform run is in
  // flight — the link may sit on a sibling record for the same service+env.
  // Fall back to the most recent one that isn't older than this run.
  const fallbackPlan = usePlanLinkFor(record);
  const planLink = record.s3_plan_link || fallbackPlan?.link;
  // Flash tint matches the new status so a live change reads at a glance.
  const flashTint = alpha(getStatusHue(record.status), theme.palette.mode === 'dark' ? 0.26 : 0.16);

  // First render plays a staggered entrance; thereafter a live change flashes
  // the row. New rows (which mount and flash at once) just get the entrance.
  const enteredRef = useRef(false);
  useEffect(() => {
    enteredRef.current = true;
  }, []);

  const [logsOpen, setLogsOpen] = useState(false);
  const animationSx = !enteredRef.current
    ? {
        animation: `${fadeSlideUp} ${duration.base}ms ${easing.emphasized} both`,
        animationDelay: `${Math.min(index, 12) * 30}ms`,
      }
    : flashed
      ? { animation: `${flash(flashTint)} 1200ms ${easing.standard}` }
      : {};

  const destroy = isDestroyOperation(record);
  const inProgress = isInProgressStatus(record.status);
  const path = servicePath(record);
  const w = { ...DEFAULT_COLUMN_WIDTHS, ...widths };
  // Ready-to-run jnlocal deploy command for this record (null if it can't be built).
  const deployCmd = buildDeployCommand(record);

  const statusLabel = destroy
    ? record.status?.toLowerCase() === 'success'
      ? 'Destroyed'
      : record.status?.toLowerCase() === 'failed'
        ? 'Destroy failed'
        : inProgress
          ? 'Destroying'
          : undefined
    : undefined;

  // Detail-line segments — each rendered only when present.
  const failed = record.status?.toLowerCase() === 'failed';
  const showDestroy = !!details && destroy;
  // Stage/step shows on the first line while in progress; the detail line
  // adds it for completed/failed runs. On success the stage (e.g.
  // "PostDeployment") is redundant next to the step ("Deployment Success"),
  // so drop it; keep it otherwise since it shows where a run is/failed.
  const succeeded = record.status?.toLowerCase() === 'success';
  const stageStepText = (succeeded ? [record.step] : [record.stage, record.step])
    .filter(Boolean)
    .join(' · ');
  const showStageStep = !!details && !inProgress && !!stageStepText;
  const showApproval = !!details && !!record.decision;
  const showProgress = !!progress && hasProgressView(record);
  // Only Argo deployments publish logs; a Step Functions or historical Tekton
  // record has nothing to show, so offer no button rather than a dead one.
  // The id is what the logs are fetched by, so a record without one would
  // offer a control that can only land on an error.
  const showLogsButton = record.metadata?.engine === 'argo' && !!record.deployment_id;
  // Always surface a failed deployment's error inline (readable + copyable).
  // The progress stepper shows *where* it failed; this shows *what* failed.
  // Compact here — the full details and the logs action live in the dialogs,
  // and this row already carries its own "View logs" button.
  const showInlineError = !!details && failed && describeError(record.error) !== null;
  const hasDetails = showDestroy || showStageStep || showApproval || showInlineError;
  const destroyTag = tagStyle(statusHues.pending, theme.palette.mode);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        borderBottom: `1px solid ${c.hairline}`,
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { backgroundColor: c.hoverWash },
        minWidth: 0,
        ...animationSx,
        ...reduceMotionSx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          // Wraps on a narrow window: the actions group below drops to its own
          // line, right-aligned, instead of being clipped off the row's end.
          flexWrap: 'wrap',
          columnGap: 1.5,
          rowGap: 0.75,
          minWidth: 0,
        }}
      >
        <StatusDot status={record.status} />

        {!hideService && (
          <Box sx={{ minWidth: 0, width: { xs: 'auto', sm: w.service }, flexShrink: 0 }}>
            {path ? (
              <MuiLink
                component={RouterLink}
                to={path}
                underline="hover"
                sx={{ display: 'block', minWidth: 0, color: c.ink, fontWeight: 600, fontSize: '0.9rem' }}
                noWrap
              >
                {record.app}
                {record.serviceName ? (
                  <Box component="span" sx={{ color: c.inkMuted, fontWeight: 400 }}>
                    {' / '}
                    {record.serviceName}
                  </Box>
                ) : null}
              </MuiLink>
            ) : (
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: c.ink }} noWrap>
                {record.service}
              </Typography>
            )}
          </Box>
        )}

        <Typography
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.78rem',
            color: c.inkSecondary,
            width: w.version,
            flexShrink: 0,
            display: VERSION_DISPLAY,
          }}
          noWrap
          title={record.version}
        >
          {record.version || '—'}
        </Typography>

        <Box sx={{ display: BADGES_DISPLAY, gap: 0.75, flexShrink: 0 }}>
          <TypeBadge type={record.type} />
          {!hideEnv && <EnvBadge env={record.envName} dynamic={record.ttl != null} />}
        </Box>

        <StatusPill status={record.status} label={statusLabel} />

        {/* Auto-rollback watch state (DEVOPS-1629) — renders nothing when absent. */}
        <WatchPill record={record} />

        {/* Job lock state (DEVOPS-1633) — a lock is current state of the
            service+env, not an attribute of a past deploy, so show it only on
            current-state rows (Activity / Overview / "Currently deployed"),
            never on the historical deployment list (`details`). */}
        {!details && lock && <LockBadge lock={lock} />}

        {/* Stage / step while deploying */}
        {inProgress && (record.stage || record.step) && (
          <Typography
            variant="caption"
            sx={{ color: c.inkMuted, display: { xs: 'none', lg: 'block' } }}
            noWrap
          >
            {[record.stage, record.step].filter(Boolean).join(' · ')}
          </Typography>
        )}

        {/* Actions + time: one unit, pushed right; wraps to its own line as a whole. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto', flexShrink: 0 }}>
          {planLink && (
            <Tooltip title="View terraform plan" arrow>
              <Button
                size="small"
                variant="outlined"
                component="a"
                href={planLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                startIcon={<PlanIcon sx={{ fontSize: 14 }} />}
                sx={{
                  flexShrink: 0,
                  minWidth: 0,
                  py: 0.1,
                  px: 0.85,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                TF Plan
              </Button>
            </Tooltip>
          )}

          {deployCmd && (
            <Tooltip title={`Copy deploy command (${deployCmd})`} arrow>
              <IconButton
                size="small"
                aria-label="Copy deploy command"
                onClick={e => {
                  e.stopPropagation();
                  navigator.clipboard?.writeText(deployCmd);
                }}
              >
                <TerminalIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}

          {showLogsButton && (
            <Tooltip title="View logs" arrow>
              <IconButton
                size="small"
                aria-label="View logs"
                onClick={e => {
                  // The row itself opens the details dialog — don't do both.
                  e.stopPropagation();
                  setLogsOpen(true);
                }}
              >
                <LogsIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Renders only for a failed Argo deployment that can be analysed. */}
          <StartRcaButton record={record} />

          {record.deployment_id && (
            <Tooltip title={`Copy deployment ID (${record.deployment_id})`} arrow>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  navigator.clipboard?.writeText(record.deployment_id || '');
                }}
              >
                <CopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}

          <Typography
            variant="caption"
            sx={{ color: c.inkMuted, width: w.time, textAlign: 'right', flexShrink: 0 }}
            title={formatTimestamp(record.timestamp)}
            noWrap
          >
            {timeAgo(record.timestamp)}
          </Typography>
        </Box>
      </Box>

      {hasDetails && (
        <Box
          sx={{
            mt: 0.5,
            // Align under the text column (status dot 8px + 12px gap).
            ml: '20px',
            minWidth: 0,
          }}
        >
          {(showDestroy || showStageStep || showApproval) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              {showDestroy && (
                <Box
                  component="span"
                  sx={{
                    px: 0.75,
                    py: 0.1,
                    borderRadius: '4px',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    backgroundColor: destroyTag.bg,
                    color: destroyTag.text,
                    border: `1px solid ${destroyTag.border}`,
                    flexShrink: 0,
                  }}
                >
                  destroy
                </Box>
              )}

              {showStageStep && (
                <Typography variant="caption" sx={{ color: c.inkMuted, flexShrink: 0 }} noWrap>
                  {stageStepText}
                </Typography>
              )}

              {showApproval && (
                <Typography variant="caption" sx={{ color: c.inkMuted, flexShrink: 0 }} noWrap>
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 600,
                      color:
                        record.decision?.toLowerCase() === 'rejected'
                          ? statusHues.failed
                          : statusHues.success,
                    }}
                  >
                    {record.decision}
                  </Box>
                  {record.decision_by ? ` by ${record.decision_by}` : ''}
                  {record.decision_timestamp ? ` · ${formatTimestamp(record.decision_timestamp)}` : ''}
                </Typography>
              )}
            </Box>
          )}

          {showInlineError && (
            <Box sx={{ mt: showDestroy || showStageStep || showApproval ? 0.5 : 0 }}>
              <DeploymentError error={record.error} record={record} compact />
            </Box>
          )}
        </Box>
      )}

      {showProgress && (
        <Box sx={{ mt: 0.75, ml: '20px', maxWidth: 520 }}>
          <DeploymentProgress record={record} />
        </Box>
      )}

      {showLogsButton && (
        <DeploymentProgress
          record={record}
          open={logsOpen}
          onClose={() => setLogsOpen(false)}
          initialView="logs"
        />
      )}
    </Box>
  );
};

export default DeploymentRow;
